import { useRef, useState } from "react";
import {
  removeRouteTrailer,
  removeRouteTruck,
  updateRouteTrailer,
  updateRouteTruck,
} from "../../../ducks/routes";
import { useSelector } from "react-redux";
import { selectRouteViewModel } from "../../../ducks/data/route-views/selectors";
import { useAppDispatch } from "../../../redux-store";
import { selectVehicles } from "../../../ducks/data/trailers/selectors";
import VehicleListAutocomplete from "./vehicle-list-autocomplete";

type VehicleChange = { id: string } | null;

const VehicleListAutocompleteForRoute = (props: {
  routeId: string;
  isCompact?: boolean;
  type: "truck" | "trailer";
}) => {
  const { routeId, type, isCompact } = props;
  const dispatch = useAppDispatch();
  const route = useSelector(selectRouteViewModel(routeId));
  const vehicles = useSelector(selectVehicles);
  // Two changes to the same route in flight at once race the route's
  // optimistic lock and the loser comes back as a 409 (DISPATCHER-8K), which
  // is what repeated picks on a slow connection used to do. So only one
  // request runs at a time; a pick made meanwhile replaces any earlier queued
  // one and is sent when the current request finishes. The store shows the
  // in-flight value optimistically; `queued` shows the latest pick until then.
  const reqInProgressRef = useRef(false);
  const queuedRef = useRef<{ change: VehicleChange } | null>(null);
  const [queued, setQueued] = useState<{ change: VehicleChange } | null>(null);

  const dispatchVehicleChange = (routeId: string, change: VehicleChange) => {
    if (type === "truck") {
      return change
        ? dispatch(updateRouteTruck({ id: routeId, truckId: change.id }))
        : dispatch(removeRouteTruck({ id: routeId }));
    }
    return change
      ? dispatch(updateRouteTrailer({ id: routeId, trailerId: change.id }))
      : dispatch(removeRouteTrailer({ id: routeId }));
  };

  const handleVehicleChange = async (change: VehicleChange) => {
    if (!route) {
      return;
    }
    if (reqInProgressRef.current) {
      queuedRef.current = { change };
      setQueued({ change });
      return;
    }
    reqInProgressRef.current = true;
    try {
      let next: { change: VehicleChange } | null = { change };
      while (next) {
        await dispatchVehicleChange(route.id, next.change);
        next = queuedRef.current;
        queuedRef.current = null;
      }
    } finally {
      reqInProgressRef.current = false;
      setQueued(null);
    }
  };

  if ((route?.truckId || route?.unpoweredTrailerId) && !vehicles?.length) {
    // If the route has a trailer, but trailers are not loaded, don't render this yet.
    return null;
  }

  const routeVehicleId =
    type === "truck"
      ? route?.truckId || null
      : route?.unpoweredTrailerId || null;

  return (
    <VehicleListAutocomplete
      routeTrailerId={queued ? queued.change?.id || null : routeVehicleId}
      handleTrailerChange={handleVehicleChange}
      isCompact={isCompact}
      type={type}
    />
  );
};

export default VehicleListAutocompleteForRoute;
